import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { KeyRound, Save, TimerReset, Sunrise, Sunset, UserX, Send, Eye, X, Ghost, ReceiptText, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { api, errMsg } from "../../lib/api";
import { staffLabelText } from "../../components/StaffAvatar";
import { useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel, ReadOnlyNotice } from "../../components/FeatureGate";
import { EffectBadge, effectOf } from "./ReceiptVisibility";   // Phase 169
import { SafEscalationSettingsCard } from "../../components/saf/SafEscalationSettingsCard";   // Phase 210
import { CollectionTargetsCard } from "../../components/dashboard/CollectionTargetsCard";   // Phase 214

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
// Phase 144: compare two HH:MM strings (IST clock) — true when `end` is strictly later than `start`
const laterThan = (end, start) => HHMM.test(end || "") && HHMM.test(start || "") && end > start;

export default function Settings() {
  const { code } = useParams();
  // Phase 152: sub-features — payment_gateway (Razorpay keys) and field_tracking (idle threshold / duty hours / absent alerts)
  const access = useRoleModules(code);
  const showGateway = access.featureAllowed("settings", "payment_gateway");
  const gatewayRO = !access.canWrite("settings", "payment_gateway");
  const showTracking = access.featureAllowed("settings", "field_tracking");
  const trackingRO = !access.canWrite("settings", "field_tracking");
  // Phase 163: placeholder collector name prefix (GESIL-1, GESIL-2 …)
  const showPlaceholder = access.featureAllowed("settings", "placeholder_collectors");
  const placeholderRO = !access.canWrite("settings", "placeholder_collectors");
  const [phSettings, setPhSettings] = useState(null);
  // Phase 169: receipt visibility shortcut card (who may open receipts)
  const showReceiptVis = access.featureAllowed("settings", "receipt_visibility");
  // Phase 210: SAF auto-escalation time limits
  const showSafEsc = access.featureAllowed("settings", "saf_escalation");
  const safEscRO = !access.canWrite("settings", "saf_escalation");
  // Phase 214: month-wise collection targets (target line on the dashboard graph)
  const showTargets = access.featureAllowed("settings", "collection_targets");
  const targetsRO = !access.canWrite("settings", "collection_targets");
  const [rvData, setRvData] = useState(null);
  const [phPrefix, setPhPrefix] = useState("");
  const [phBusy, setPhBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ razorpay_key_id: "", razorpay_key_secret: "", idle_threshold_minutes: 60, duty_start_time: "10:00",
    duty_end_time: "", absent_alert_enabled: true, absent_alert_skip_sunday: true });
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/t/${code}/settings`).then((r) => {
    setSettings(r.data);
    setForm({ razorpay_key_id: r.data.razorpay_key_id || "", razorpay_key_secret: r.data.razorpay_key_secret || "",
      idle_threshold_minutes: r.data.idle_threshold_minutes || 60, duty_start_time: r.data.duty_start_time || "10:00",
      duty_end_time: r.data.duty_end_time || "",     // Phase 144: "" = no end time configured (no after-hours cutoff)
      absent_alert_enabled: r.data.absent_alert_enabled !== false, absent_alert_skip_sunday: r.data.absent_alert_skip_sunday !== false });
  });
  useEffect(() => { load().catch(() => {}); }, [code]); // eslint-disable-line

  // Phase 163: prefix card loads independently so a "no access" on it never blocks the other cards
  useEffect(() => {
    if (!showPlaceholder) return;
    api.get(`/t/${code}/settings/placeholder-collectors`)
      .then((r) => { setPhSettings(r.data); setPhPrefix(r.data.prefix || "GESIL"); })
      .catch(() => setPhSettings({ error: true }));
  }, [code, showPlaceholder]);
  useEffect(() => {
    if (!showReceiptVis) return;
    api.get(`/t/${code}/settings/receipt-visibility`).then((r) => setRvData(r.data)).catch(() => setRvData({ error: true }));
  }, [code, showReceiptVis]);
  const normalizedPrefix = phPrefix.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const prefixInvalid = !normalizedPrefix || normalizedPrefix.length > 12;
  const savePrefix = async () => {
    if (prefixInvalid) { toast.error("Prefix must be 1–12 letters or digits"); return; }
    setPhBusy(true);
    try {
      const r = await api.put(`/t/${code}/settings/placeholder-collectors`, { prefix: normalizedPrefix });
      setPhSettings((s) => ({ ...(s || {}), prefix: r.data.prefix }));
      setPhPrefix(r.data.prefix);
      toast.success("Placeholder prefix saved", { description: r.data.message });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPhBusy(false);
    }
  };

  // Phase 144: duty window helpers for inline validation + copy
  const dutyStart = HHMM.test(form.duty_start_time || "") ? form.duty_start_time : "10:00";
  const dutyEnd = HHMM.test(form.duty_end_time || "") ? form.duty_end_time : "";
  const endInvalid = !!dutyEnd && !laterThan(dutyEnd, dutyStart);

  // Phase 152: each card saves only its own keys so the two sub-features can be granted independently
  const saveGateway = async () => {
    setBusy(true);
    try {
      await api.put(`/t/${code}/settings`, { razorpay_key_id: form.razorpay_key_id, razorpay_key_secret: form.razorpay_key_secret });
      toast.success("Payment gateway settings saved");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (endInvalid) {
      toast.error(`Duty end time (${dutyEnd}) must be later than the duty start time (${dutyStart})`);
      return;
    }
    setBusy(true);
    try {
      const threshold = Math.max(10, Math.min(480, parseInt(form.idle_threshold_minutes, 10) || 60));
      // duty_end_time "" clears the end time on the server (= no after-hours cutoff)
      await api.put(`/t/${code}/settings/tracking`, {
        idle_threshold_minutes: threshold, duty_start_time: dutyStart, duty_end_time: dutyEnd,
        absent_alert_enabled: form.absent_alert_enabled, absent_alert_skip_sunday: form.absent_alert_skip_sunday,
      });
      toast.success("Settings saved");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  // Phase 89: absent-agent alert helpers
  const loadPreview = async () => {
    setPreviewBusy(true);
    try {
      const r = await api.get(`/t/${code}/tracking/absent-alerts/preview`);
      setPreview(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPreviewBusy(false);
    }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post(`/t/${code}/tracking/absent-alerts/check?force=true`);
      const d = r.data;
      if (d.skipped) toast.info(d.reason === "disabled" ? "Absent agent alert is turned off — enable and save first" : `Skipped: ${d.reason}`);
      else if (d.alerts_sent > 0) toast.success(`Sent ${d.alerts_sent} alert${d.alerts_sent === 1 ? "" : "s"} — ${d.collectors_absent} of ${d.collectors_total} collectors not signed in`);
      else if (d.already_sent > 0) toast.info("Today's alert was already sent to everyone who needed it");
      else toast.success("Nothing to send — every collector has signed in today");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  if (!settings) return <Spinner />;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Municipality configuration and payment gateway" />
      {access.loaded && !showGateway && !showTracking && !showPlaceholder && !showReceiptVis && !showSafEsc && !showTargets && <NoAccessPanel what="Settings" testId="settings-no-access" />}
      {/* Phase 214: monthly collection targets — drawn as the target line on the dashboard graph */}
      {showTargets && (
        <div className="space-y-2 mb-4 max-w-3xl" data-testid="settings-collection-targets-section">
          {targetsRO && <ReadOnlyNotice what="the monthly collection targets" testId="settings-collection-targets-readonly-notice" />}
          <CollectionTargetsCard code={code} readOnly={targetsRO} testIdPrefix="settings-collection-targets" />
        </div>
      )}
      {showReceiptVis && (
        <Card className="shadow-sm max-w-xl mb-4 border-primary/20" data-testid="settings-receipt-visibility-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <ReceiptText className="h-4 w-4" /> Receipt Visibility
              {rvData && !rvData.error && rvData.roles_order && (() => {
                const hidden = rvData.roles_order.filter((k) => !effectOf({ ...rvData.roles[k], ...Object.fromEntries(["window_from", "window_to", "payment_from", "payment_to"].map((d) => [d, rvData.roles[k][d] || ""])) }, rvData.today).visible).length;
                return hidden === 0
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline" data-testid="settings-receipt-visibility-status">Visible to all</Badge>
                  : <Badge className="bg-amber-50 text-amber-800 border-amber-200" variant="outline" data-testid="settings-receipt-visibility-status">Hidden for {hidden} role{hidden === 1 ? "" : "s"}</Badge>;
              })()}
            </CardTitle>
            <CardDescription className="text-xs">Choose whether citizens, tax collectors and supervisors can open, print or download payment receipts — with optional date ranges. Receipts stay safely on file either way.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {rvData?.error && <p className="text-xs text-destructive" data-testid="settings-receipt-visibility-error">Could not load the receipt visibility settings.</p>}
            {rvData && !rvData.error && rvData.roles_order && (
              <ul className="divide-y rounded-lg border text-sm" data-testid="settings-receipt-visibility-roles">
                {rvData.roles_order.map((k) => {
                  const rule = { ...rvData.roles[k], ...Object.fromEntries(["window_from", "window_to", "payment_from", "payment_to"].map((d) => [d, rvData.roles[k][d] || ""])) };
                  const eff = effectOf(rule, rvData.today);
                  return (
                    <li key={k} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`settings-receipt-visibility-role-${k}`}>
                      <div className="min-w-0"><p className="font-medium">{rvData.role_labels[k]}</p><p className="text-[11px] text-muted-foreground truncate" title={eff.text}>{eff.text}</p></div>
                      <EffectBadge visible={eff.visible} testId={`settings-receipt-visibility-badge-${k}`} />
                    </li>
                  );
                })}
              </ul>
            )}
            <Button asChild size="sm" data-testid="settings-receipt-visibility-manage">
              <Link to={`/${code}/admin/receipt-visibility`}>Manage receipt visibility <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
          </CardContent>
        </Card>
      )}
      {showGateway && <Card className="shadow-sm max-w-xl" data-testid="settings-gateway-card">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Razorpay Payment Gateway
            {settings.razorpay_configured ? (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline">Configured</Badge>
            ) : (
              <Badge variant="outline" className="bg-slate-100 text-slate-600">Not configured</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Add your Razorpay API keys to enable online payments (UPI, cards, net banking) for citizens.
            Until configured, citizens can pay via field collectors or at the municipal counter.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {gatewayRO && <ReadOnlyNotice what="the payment gateway settings" testId="settings-gateway-readonly-notice" />}
          <fieldset disabled={gatewayRO} className="space-y-3 min-w-0">
          <div className="space-y-1">
            <Label>Razorpay Key ID</Label>
            <Input value={form.razorpay_key_id} onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value })}
              placeholder="rzp_live_xxxxxxxxxx" data-testid="razorpay-key-id-input" />
          </div>
          <div className="space-y-1">
            <Label>Razorpay Key Secret</Label>
            <Input type="password" value={form.razorpay_key_secret} onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value })}
              placeholder="Secret key" data-testid="razorpay-key-secret-input" />
            <p className="text-xs text-muted-foreground">Stored securely per municipality. Never shared with the central platform.</p>
          </div>
          {!gatewayRO && <Button onClick={saveGateway} disabled={busy} data-testid="settings-save-button">
            <Save className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Save settings"}
          </Button>}
          </fieldset>
        </CardContent>
      </Card>}

      {showTracking && <Card className="shadow-sm max-w-xl mt-4" data-testid="settings-tracking-card">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <TimerReset className="h-4 w-4" /> Field Tracking — Idle Alerts
          </CardTitle>
          <CardDescription>
            Flag field agents whose GPS has not moved (or stopped pinging) for longer than this threshold
            during duty hours. Alerts appear on the Agent Tracking map and as bell notifications — never outside the duty window below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {trackingRO && <ReadOnlyNotice what="field tracking settings" testId="settings-tracking-readonly-notice" />}
          <fieldset disabled={trackingRO} className="space-y-3 min-w-0">
          <div className="space-y-1">
            <Label>Idle threshold (minutes)</Label>
            <Input type="number" min="10" max="480" value={form.idle_threshold_minutes}
              onChange={(e) => setForm({ ...form, idle_threshold_minutes: e.target.value })}
              data-testid="idle-threshold-input" />
            <p className="text-xs text-muted-foreground">Default 60 minutes. Allowed range: 10–480.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2" data-testid="duty-hours-fields">
            <div className="space-y-1">
              <Label className="flex items-center gap-1.5" htmlFor="duty-start-time-input"><Sunrise className="h-3.5 w-3.5 text-muted-foreground" /> Duty start time (IST)</Label>
              <Input id="duty-start-time-input" type="time" value={form.duty_start_time} step="300"
                onChange={(e) => setForm({ ...form, duty_start_time: e.target.value })}
                className="w-40" data-testid="duty-start-time-input" />
              <p className="text-xs text-muted-foreground">
                First sign-in after this time is marked as a <span className="font-medium text-amber-700">Late start</span>. Default 10:00.
              </p>
            </div>
            {/* Phase 144: duty END time — optional, no product default; the admin decides */}
            <div className="space-y-1">
              <Label className="flex items-center gap-1.5" htmlFor="duty-end-time-input"><Sunset className="h-3.5 w-3.5 text-muted-foreground" /> Duty end time (IST)</Label>
              <div className="flex items-center gap-1.5">
                <Input id="duty-end-time-input" type="time" value={form.duty_end_time || ""} step="300"
                  onChange={(e) => setForm({ ...form, duty_end_time: e.target.value })}
                  aria-invalid={endInvalid ? "true" : undefined}
                  className={`w-40 ${endInvalid ? "border-red-400 focus-visible:ring-red-400" : ""}`} data-testid="duty-end-time-input" />
                {form.duty_end_time && (
                  <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-xs text-muted-foreground"
                    onClick={() => setForm({ ...form, duty_end_time: "" })} aria-label="Clear duty end time" data-testid="duty-end-time-clear-button">
                    <X className="h-3.5 w-3.5 mr-1" /> Clear
                  </Button>
                )}
              </div>
              {endInvalid ? (
                <p className="text-xs text-red-700 font-medium" role="alert" data-testid="duty-end-time-error">
                  End time must be later than the start time ({dutyStart}).
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">Optional. Leave blank for no after-hours cutoff.</p>
              )}
            </div>
          </div>
          <p className={`text-xs rounded-md border px-2.5 py-2 ${dutyEnd && !endInvalid ? "bg-emerald-50/60 border-emerald-200 text-emerald-900" : "bg-muted/40 text-muted-foreground"}`}
            data-testid="duty-hours-summary" data-duty-end-set={dutyEnd && !endInvalid ? "true" : "false"}>
            {dutyEnd && !endInvalid ? (
              <>Idle &amp; absent alerts are active <span className="font-semibold tabular-nums">{dutyStart}–{dutyEnd} IST</span>. After {dutyEnd} nothing is
                sent and the Idle History ignores stillness outside these hours.</>
            ) : (
              <>No end time set — idle alerts can fire from {dutyStart} IST until midnight. Set a duty end time to stop idle &amp; absent alerts after hours.</>
            )}
          </p>

          <div className="rounded-lg border bg-muted/30 p-3 space-y-3" data-testid="absent-alert-section">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5"><UserX className="h-4 w-4 text-muted-foreground" /> Absent agent alert</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  At the duty start time ({dutyStart} IST) each supervisor gets a bell notification listing their collectors who have not
                  signed in; Municipal Admins get one municipality-wide summary grouped by supervisor. Sent once a day, only when someone is absent
                  {dutyEnd && !endInvalid ? <>, and never after {dutyEnd} IST</> : null}.
                </p>
              </div>
              <Switch checked={!!form.absent_alert_enabled} onCheckedChange={(v) => setForm({ ...form, absent_alert_enabled: v })}
                aria-label="Absent agent alert enabled" data-testid="absent-alert-enabled-switch" />
            </div>
            <label className="flex items-center justify-between gap-3 text-sm cursor-pointer select-none">
              <span>Skip Sundays</span>
              <Switch checked={!!form.absent_alert_skip_sunday} onCheckedChange={(v) => setForm({ ...form, absent_alert_skip_sunday: v })}
                disabled={!form.absent_alert_enabled} aria-label="Skip Sundays" data-testid="absent-alert-skip-sunday-switch" />
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" variant="outline" onClick={loadPreview} disabled={previewBusy} data-testid="absent-alert-preview-button">
                <Eye className="h-3.5 w-3.5 mr-1.5" /> {previewBusy ? "Checking…" : "Preview today's list"}
              </Button>
              {!trackingRO && <Button type="button" size="sm" variant="secondary" onClick={sendNow} disabled={sending} data-testid="absent-alert-send-now-button">
                <Send className="h-3.5 w-3.5 mr-1.5" /> {sending ? "Sending…" : "Send today's alert now"}
              </Button>}
            </div>
            {preview && (
              <div className="rounded-md border bg-background p-2.5 text-xs space-y-1.5" data-testid="absent-alert-preview">
                <p className="font-medium" data-testid="absent-alert-preview-summary">
                  {preview.collectors_absent} of {preview.collectors_total} collector{preview.collectors_total === 1 ? "" : "s"} not signed in today ({preview.day})
                </p>
                {preview.groups.filter((g) => g.absent.length > 0).map((g, i) => (
                  <p key={g.supervisor_id || `u${i}`} data-testid={`absent-alert-preview-group-${g.supervisor_id || "unassigned"}`}>
                    <span className="text-muted-foreground">{g.supervisor_name ? `${staffLabelText(g.supervisor_name, g.supervisor_employee_id)}'s team` : "Unassigned"}:</span>{" "}
                    <span className="text-red-700 font-medium">{g.absent.map((a) => staffLabelText(a.name, a.employee_id)).join(", ")}</span>
                  </p>
                ))}
                {preview.absent_supervisors?.length > 0 && (
                  <p data-testid="absent-alert-preview-supervisors"><span className="text-muted-foreground">Supervisors not signed in:</span>{" "}
                    <span className="text-red-700 font-medium">{preview.absent_supervisors.map((a) => staffLabelText(a.name, a.employee_id)).join(", ")}</span></p>
                )}
                {preview.collectors_absent === 0 && preview.absent_supervisors?.length === 0 && (
                  <p className="text-emerald-700">Everyone has signed in today.</p>
                )}
              </div>
            )}
          </div>
          {!trackingRO && <Button onClick={save} disabled={busy} variant="outline" data-testid="idle-threshold-save-button">
            <Save className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Save settings"}
          </Button>}
          </fieldset>
        </CardContent>
      </Card>}

      {/* Phase 163: placeholder collectors — the name prefix for auto-created stand-in collectors */}
      {showPlaceholder && <Card className="shadow-sm max-w-xl mt-4" data-testid="settings-placeholder-card">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Ghost className="h-4 w-4" /> Placeholder collectors
            {phSettings && !phSettings.error && (
              <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-200 tabular-nums" data-testid="settings-placeholder-count">
                {phSettings.placeholders} active
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Every zone that has a ward with no active collector gets one stand-in staff entry automatically
            (named <span className="font-mono font-medium text-foreground">{normalizedPrefix || "GESIL"}-&lt;ZONE&gt;</span>, e.g. <span className="font-mono font-medium text-foreground">{normalizedPrefix || "GESIL"}-ZONE-1</span>;
            wards without a zone share <span className="font-mono font-medium text-foreground">{normalizedPrefix || "GESIL"}-UNZONED</span>)
            so the money shows up in Collector Targets and reports. Placeholders cannot sign in; when a real collector is mapped to a ward, that
            ward's data moves to them automatically (see Users &amp; Staff).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {placeholderRO && <ReadOnlyNotice what="the placeholder collector settings" testId="settings-placeholder-readonly-notice" />}
          {phSettings?.error ? (
            <p className="text-xs text-destructive" data-testid="settings-placeholder-error">Could not load the placeholder settings.</p>
          ) : (
          <fieldset disabled={placeholderRO || !phSettings} className="space-y-3 min-w-0">
            <div className="space-y-1">
              <Label htmlFor="placeholder-prefix-input">Name prefix</Label>
              <Input id="placeholder-prefix-input" value={phPrefix} onChange={(e) => setPhPrefix(e.target.value)} maxLength={12}
                placeholder="GESIL" className="w-48 font-mono uppercase" aria-invalid={prefixInvalid && phPrefix ? "true" : undefined}
                data-testid="placeholder-prefix-input" />
              <p className={`text-xs ${prefixInvalid && phPrefix ? "text-red-700 font-medium" : "text-muted-foreground"}`} data-testid="placeholder-prefix-hint">
                {prefixInvalid && phPrefix
                  ? "1–12 letters, digits or hyphens only."
                  : `Letters, digits or hyphens, up to 12 characters. Existing placeholders keep their current names; new ones will be named ${normalizedPrefix || "GESIL"}-<ZONE NAME>.`}
              </p>
            </div>
            {!placeholderRO && <Button onClick={savePrefix} disabled={phBusy || prefixInvalid || normalizedPrefix === (phSettings?.prefix || "")} variant="outline" data-testid="placeholder-prefix-save-button">
              <Save className="h-4 w-4 mr-2" /> {phBusy ? "Saving..." : "Save prefix"}
            </Button>}
          </fieldset>
          )}
        </CardContent>
      </Card>}

      {/* Phase 210: SAF auto-escalation time limits */}
      {showSafEsc && (
        <div className="space-y-2 mt-4 max-w-xl" data-testid="settings-saf-escalation-section">
          {safEscRO && <ReadOnlyNotice what="the SAF escalation limits" testId="settings-saf-escalation-readonly-notice" />}
          <SafEscalationSettingsCard code={code} readOnly={safEscRO} testIdPrefix="settings-saf-escalation" />
        </div>
      )}
    </div>
  );
}
